<template>
    <div class="meta-leads-connection">
        <vs-card class="p-5">
            <div slot="header">
                <h3>Connexion Meta Business</h3>
            </div>

            <!-- Étape 1 : Bouton de connexion -->
            <div v-if="step === 'disconnected' && !loading">
                <p class="mb-4">Connectez votre compte Meta pour créer automatiquement un membre essai à chaque nouveau prospect.</p>
                <vs-button 
                    color="primary" 
                    type="filled" 
                    size="large"
                    @click="connectMeta"
                >
                    <i class="fab fa-facebook mr-2"></i>
                    Connecter mon compte Meta
                </vs-button>
            </div>

            <!-- Étape 2 : Sélection des pages -->
            <div v-if="step === 'select-pages' && !loading">
                <vs-alert color="primary" active="true" class="mb-4">
                    <strong>Sélectionnez les pages</strong> dont vous souhaitez récupérer les prospects.
                </vs-alert>

                <!-- Autorisations accordées mais incomplètes : la connexion fonctionnera,
                     certaines fonctions resteront indisponibles. -->
                <vs-alert v-if="permissionWarning" color="warning" active="true" class="mb-4">
                    {{ permissionWarning }}
                </vs-alert>

                <!-- /me/accounts a répondu une liste vide. Sélectionner une Page dans la
                     fenêtre Facebook ne suffit pas : Meta ne renvoie ici que les Pages où
                     le compte a un rôle ASSIGNÉ. Un accès hérité du portefeuille
                     professionnel laisse la Page invisible, d'où ce message détaillé
                     plutôt qu'un « aucune page trouvée » sans issue. -->
                <div v-if="availablePages.length === 0" class="p-6">
                    <vs-alert color="warning" active="true" class="mb-4">
                        <strong>Aucune Page accessible avec ce compte Meta.</strong>
                    </vs-alert>

                    <p class="mb-3">Meta n'a renvoyé aucune Page pour ce compte, même si vous en avez
                        sélectionné une dans la fenêtre Facebook. C'est le cas quand le compte n'a pas
                        de rôle directement assigné sur la Page.</p>

                    <p class="mb-3">À vérifier dans <strong>Meta Business Suite → Paramètres → Comptes →
                        Pages</strong> : sélectionnez votre Page, puis <strong>Personnes ayant accès</strong>.
                        Votre compte Facebook personnel doit y figurer avec le <strong>contrôle total</strong>.
                        Un accès hérité du portefeuille professionnel ne suffit pas — il faut une
                        assignation explicite sur la Page.</p>

                    <p class="mb-4 text-sm text-gray-500">Vérifiez également que vous êtes connecté au bon
                        compte Facebook : la fenêtre de connexion utilise la session ouverte dans ce navigateur.</p>

                    <vs-button color="primary" @click="connectMeta">Réessayer</vs-button>
                    <vs-button class="ml-2" color="danger" type="border" @click="cancelPageSelection">Annuler</vs-button>
                </div>

                <div v-else>
                    <div 
                        v-for="page in availablePages" :key="page.id" 
                        class="page-select-item mb-2 p-3 border rounded cursor-pointer"
                        :class="{ 'border-primary bg-primary-light': selectedPageIds.includes(page.id) }"
                        @click="togglePage(page.id)"
                    >
                        <div class="flex items-center">
                            <vs-checkbox 
                                :value="selectedPageIds.includes(page.id)" 
                                @click.stop="togglePage(page.id)"
                                class="mr-3"
                            />
                            <img 
                                v-if="page.picture && page.picture.data" 
                                :src="page.picture.data.url" 
                                :alt="page.name"
                                class="w-10 h-10 rounded-full mr-3"
                            />
                            <div>
                                <h5>{{ page.name }}</h5>
                                <p class="text-sm text-gray-500">{{ page.category || 'Page Facebook' }}</p>
                            </div>
                        </div>
                    </div>

                    <div class="mt-4 flex justify-between">
                        <vs-button color="danger" type="border" @click="cancelPageSelection">Annuler</vs-button>
                        <vs-button 
                            color="primary" 
                            :disabled="selectedPageIds.length === 0"
                            @click="confirmPageSelection"
                        >
                            Valider ({{ selectedPageIds.length }} page(s))
                        </vs-button>
                    </div>
                </div>
            </div>

            <!-- Étape 3 : Connecté -->
            <div v-if="step === 'connected' && !loading">
                <div class="flex justify-between items-center mb-4 p-4 border rounded bg-green-50">
                    <div class="flex flex-col items-start">
                        <vs-chip color="success">
                            <i class="material-icons">check_circle</i>
                            Compte Meta connecté
                        </vs-chip>
                        <p class="mt-2 text-sm">{{ connectedPages.length }} page(s) — les nouveaux prospects créent automatiquement un membre prospect.</p>
                    </div>
                    <div class="flex gap-2">
                        <vs-button color="primary" type="border" @click="addPages">
                            <span class="flex items-center">
                                <i class="material-icons mr-1">add_circle</i>
                                Ajouter des pages
                            </span>
                        </vs-button>
                        <vs-button color="danger" type="border" @click="disconnect">
                            Déconnecter
                        </vs-button>
                    </div>
                </div>

                <!-- Résultat import manuel -->
                <vs-alert v-if="importResult" :color="importResult.count > 0 ? 'success' : 'warning'" active="true" class="mb-4">
                    <span v-if="importResult.count > 0">
                        <strong>{{ importResult.count }} lead(s)</strong> importé(s) et créé(s) en tant que membre prospect pour {{ importResult.pageName }}.
                    </span>
                    <span v-else>
                        Aucun nouveau lead à importer pour {{ importResult.pageName }}.
                    </span>
                </vs-alert>

                <div v-for="(page, index) in connectedPages" :key="page.facebook_page_id" class="page-item mb-3 p-3 border rounded">
                    <div class="flex items-center justify-between">
                        <div class="flex items-center">
                            <img 
                                v-if="page.picture_url" 
                                :src="page.picture_url" 
                                :alt="page.name"
                                class="w-10 h-10 rounded-full mr-3"
                            />
                            <div>
                                <h5>{{ page.name }}</h5>
                                <vs-chip :color="page.webhook_active ? 'success' : 'warning'" size="small" class="mt-1">
                                    {{ page.webhook_active ? 'Webhook actif' : 'Webhook en attente' }}
                                </vs-chip>
                                <!-- Cause exacte du blocage, au lieu d'un « en attente » muet. -->
                                <p v-if="page.error_message" class="text-sm text-danger mt-1">{{ page.error_message }}</p>
                                <p v-else-if="page.lead_access_warning" class="text-sm text-warning mt-1">{{ page.lead_access_warning }}</p>
                            </div>
                        </div>
                        <div class="flex items-center gap-2">
                            <!-- Import historique masqué : GET /{form_id}/leads exige pages_manage_ads
                                 (non encore approuvé). Réactiver en passant importEnabled à true une fois
                                 la nouvelle vérification Meta validée. Les nouveaux leads restent captés
                                 en temps réel via le webhook. -->
                            <vs-button v-if="importEnabled" color="primary" type="flat" size="" @click="importLeads(page.facebook_page_id, page.name)" :disabled="importingPageId === page.facebook_page_id">
                                <span class="flex items-center">
                                    <i class="material-icons mr-1">cloud_download</i>
                                    Importer les leads
                                </span>
                            </vs-button>
                            <vs-button v-if="confirmDeletePageId !== page.facebook_page_id" color="danger" type="flat" size="" @click="confirmDeletePageId = page.facebook_page_id">
                                <i class="material-icons">close</i>
                            </vs-button>
                            <template v-else>
                                <span class="text-lg text-danger mr-1">Supprimer ?</span>
                                <vs-button color="danger" type="filled" size="" @click="disconnectPage(page.facebook_page_id, index)">
                                    Oui
                                </vs-button>
                                <vs-button color="primary" type="border" size="" @click="confirmDeletePageId = null">
                                    Non
                                </vs-button>
                            </template>
                        </div>
                    </div>
                </div>

                <!-- Import historique par fichier (en attendant l'import API / pages_manage_ads).
                     Les nouveaux prospects arrivent automatiquement via le webhook ; ce bloc ne
                     sert qu'au backfill des prospects antérieurs à la connexion. -->
                <div class="mt-6 p-4 border rounded">
                    <h5 class="mb-2">Importer l'historique des prospects</h5>
                    <vs-alert color="primary" active="true" class="mb-4">
                        Les nouveaux prospects sont créés <strong>automatiquement</strong> dès qu'un formulaire est rempli.
                        Pour récupérer vos prospects <strong>antérieurs</strong> à la connexion, exportez-les depuis Meta
                        (Bibliothèque de formulaires → Télécharger) puis déposez le fichier CSV ou Excel ci-dessous.
                    </vs-alert>

                    <import-excel v-if="!csvImporting" :on-success="handleCsvParsed" />

                    <div v-if="csvImporting" class="text-center p-6">
                        <vs-progress indeterminate color="primary"></vs-progress>
                        <p class="mt-3">Import en cours...</p>
                    </div>

                    <vs-alert v-if="csvResult" :color="csvResult.imported > 0 ? 'success' : 'warning'" active="true" class="mt-4">
                        <strong>{{ csvResult.imported }} prospect(s)</strong> créé(s) sur {{ csvResult.total }} ligne(s).
                        <span v-if="csvResult.duplicates"> {{ csvResult.duplicates }} doublon(s) ignoré(s).</span>
                        <span v-if="csvResult.skipped"> {{ csvResult.skipped }} ligne(s) sans contact ignorée(s).</span>
                    </vs-alert>
                </div>
            </div>

            <!-- Chargement -->
            <div v-if="loading" class="text-center p-6">
                <vs-progress indeterminate color="primary"></vs-progress>
                <p class="mt-4">{{ loadingMessage }}</p>
            </div>

            <!-- Erreur -->
            <vs-alert v-if="error" color="danger" active="true" class="mt-4">
                {{ error }}
            </vs-alert>
        </vs-card>
    </div>
</template>

<style lang="scss" scoped>
.meta-leads-connection {
    .page-item, .page-select-item {
        transition: all 0.3s ease;
        &:hover { box-shadow: 0 4px 8px rgba(0,0,0,0.1); }
    }
    .page-select-item { user-select: none; }
    .border-primary { border-color: rgba(var(--vs-primary), 1) !important; }
    .bg-primary-light { background-color: rgba(var(--vs-primary), 0.05); }
}
</style>

<script>
import facebookLeadsService from '@/services/facebookAuth';
import MetaLead from '@/database/models/meta_lead';
import ImportExcel from '@/components/excel/ImportExcel.vue';

export default {
    name: 'MetaLeadsConnection',
    components: { ImportExcel },
    data() {
        return {
            step: 'disconnected',
            loading: false,
            loadingMessage: '',
            error: null,
            availablePages: [],
            selectedPageIds: [],
            connectedPages: [],
            metaToken: null,
            importing: false,
            importingPageId: null,
            importResult: null,
            // Bouton d'import historique masqué tant que pages_manage_ads n'est pas approuvé
            // (voir commentaire dans le template). Passer à true après validation Meta.
            importEnabled: false,
            csvImporting: false,
            csvResult: null,
            confirmDeletePageId: null,
            permissionAudit: null
        };
    },
    computed: {
        // Autorisations suffisantes pour connecter, mais incomplètes : on prévient
        // sans bloquer (l'audit bloquant, lui, remplit `error`).
        permissionWarning() {
            const a = this.permissionAudit;
            if (!a || !a.ok) return null;

            // missing_import est volontairement ignoré ici : pages_manage_ads
            // n'est pas encore obtenable pour l'app, l'avertissement s'afficherait
            // donc à chaque connexion pour un problème que le client ne peut pas
            // corriger. L'info reste dans la réponse API pour le diagnostic.
            const parts = [];
            if (a.missing_recommended && a.missing_recommended.length)
                parts.push('autorisations Pages incomplètes (' + a.missing_recommended.join(', ') + ')');

            if (!parts.length) return null;
            return 'Connexion possible, mais : ' + parts.join(' ; ') + '.';
        }
    },
    mounted() {
        this.loadConnection();
    },
    methods: {
        async loadConnection() {
            try {
                const result = await MetaLead.getConnection();
                if (result && result.data && result.data.length > 0) {
                    this.connectedPages = result.data;
                    this.step = 'connected';
                }
            } catch {
                // Pas de connexion existante
            }
        },

        // Renvoie false si une autorisation bloquante manque : inutile d'aller
        // plus loin, la connexion ne remonterait aucun prospect.
        async auditPermissions(accessToken) {
            try {
                this.permissionAudit = await facebookLeadsService.checkPermissions(accessToken);
            } catch (e) {
                // Audit indisponible : on laisse le backend trancher à l'enregistrement.
                this.permissionAudit = null;
                return true;
            }

            if (this.permissionAudit && !this.permissionAudit.ok) {
                this.error = this.permissionAudit.message;
                this.step = this.connectedPages.length > 0 ? 'connected' : 'disconnected';
                return false;
            }
            return true;
        },

        async connectMeta() {
            this.loading = true;
            this.loadingMessage = 'Connexion à Meta...';
            this.error = null;
            try {
                const accessToken = await facebookLeadsService.login();
                // Le backend échange ce token en long-lived et récupère des page tokens
                // non-expirants au moment de la sauvegarde — on ne stocke jamais de token court.
                this.metaToken = accessToken;

                // Meta n'échoue pas quand une autorisation est refusée : il la retire
                // du token. On le détecte ici plutôt qu'à l'arrivée du premier lead.
                this.loadingMessage = 'Vérification des autorisations Meta...';
                const permsOk = await this.auditPermissions(accessToken);
                if (!permsOk) return;

                this.loadingMessage = 'Récupération des pages...';
                this.availablePages = await facebookLeadsService.getPages(accessToken);
                this.selectedPageIds = [];
                this.step = 'select-pages';
            } catch (error) {
                this.error = error.message || 'Erreur lors de la connexion à Meta';
            } finally {
                this.loading = false;
                this.loadingMessage = '';
            }
        },

        async addPages() {
            this.loading = true;
            this.loadingMessage = 'Connexion à Meta...';
            this.error = null;
            try {
                const accessToken = await facebookLeadsService.login();
                // Le backend échange ce token en long-lived et récupère des page tokens
                // non-expirants au moment de la sauvegarde — on ne stocke jamais de token court.
                this.metaToken = accessToken;

                this.loadingMessage = 'Vérification des autorisations Meta...';
                const permsOk = await this.auditPermissions(accessToken);
                if (!permsOk) return;

                this.loadingMessage = 'Récupération des pages...';
                this.availablePages = await facebookLeadsService.getPages(accessToken);

                // Pré-cocher les pages déjà connectées
                this.selectedPageIds = this.connectedPages.map(p => p.facebook_page_id);

                this.step = 'select-pages';
            } catch (error) {
                this.error = error.message || 'Erreur lors de la connexion à Meta';
            } finally {
                this.loading = false;
                this.loadingMessage = '';
            }
        },

        togglePage(pageId) {
            const idx = this.selectedPageIds.indexOf(pageId);
            if (idx === -1) this.selectedPageIds.push(pageId);
            else this.selectedPageIds.splice(idx, 1);
        },

        async confirmPageSelection() {
            this.loading = true;
            this.loadingMessage = 'Enregistrement des pages et activation des webhooks...';
            this.error = null;
            try {
                const selectedPages = this.availablePages
                    .filter(p => this.selectedPageIds.includes(p.id))
                    .map(p => ({
                        facebook_page_id: p.id,
                        name: p.name,
                        access_token: p.access_token,
                        picture_url: (p.picture && p.picture.data && p.picture.data.url) || null
                    }));

                const result = await MetaLead.saveConnection({
                    access_token: this.metaToken,
                    pages: selectedPages
                });

                this.connectedPages = (result && result.data) || selectedPages;
                this.step = 'connected';
                this.$emit('connected', this.connectedPages);

                this.$vs.notify({
                    title: 'Meta connecté',
                    text: `${this.connectedPages.length} page(s) connectée(s). Les prospects seront importés automatiquement.`,
                    color: 'success'
                });
            } catch (error) {
                this.error = error.message || 'Erreur lors de l\'enregistrement';
            } finally {
                this.loading = false;
                this.loadingMessage = '';
            }
        },

        async importLeads(pageId, pageName) {
            this.importing = true;
            this.importingPageId = pageId;
            this.importResult = null;
            this.error = null;
            try {
                const result = await MetaLead.importLeads(pageId);
                const importedCount = result && result.data ? result.data.length : 0;
                this.importResult = { count: importedCount, pageName: pageName };

                if (importedCount > 0) {
                    this.$vs.notify({
                        title: 'Import terminé',
                        text: `${importedCount} lead(s) importé(s) pour ${pageName}.`,
                        color: 'success'
                    });
                } else {
                    this.$vs.notify({
                        title: 'Import terminé',
                        text: `Aucun nouveau lead pour ${pageName}.`,
                        color: 'warning'
                    });
                }
            } catch (error) {
                this.error = error.message || 'Erreur lors de l\'import des leads';
            } finally {
                this.importing = false;
                this.importingPageId = null;
            }
        },

        // Mappe une ligne de CSV/Excel (objet {colonne: valeur}) vers un contact.
        // Mêmes règles que extractLeadFields côté backend : match par sous-chaîne pour
        // tolérer les en-têtes français/personnalisés (e-mail, prénom_et_nom, numéro...).
        extractLeadFromRow(row) {
            const fields = Object.keys(row || {}).map(k => ({
                key: String(k).toLowerCase(),
                val: row[k] == null ? '' : String(row[k])
            }));
            const pick = (pred) => {
                for (let i = 0; i < fields.length; i++)
                    if (pred(fields[i].key, fields[i].val)) return fields[i].val;
                return '';
            };

            let email = pick(k => k.indexOf('mail') !== -1);
            if (!email) email = pick((k, v) => /\S+@\S+\.\S+/.test(v));

            const phone = pick(k =>
                k.indexOf('phone') !== -1 || k.indexOf('mobile') !== -1 ||
                k.indexOf('tel') !== -1 || k.indexOf('tél') !== -1 ||
                k.indexOf('téléphone') !== -1 || k.indexOf('telephone') !== -1 ||
                k.indexOf('numéro') !== -1 || k.indexOf('numero') !== -1);

            let firstName = '', lastName = '';
            const full = pick(k =>
                k.indexOf('full_name') !== -1 || k.indexOf('full name') !== -1 ||
                k.indexOf('et_nom') !== -1 || k.indexOf('et nom') !== -1 ||
                k.indexOf('nom_complet') !== -1 || k.indexOf('nom complet') !== -1 ||
                k === 'name' || k === 'nom');
            if (full) {
                const parts = full.trim().split(/\s+/);
                firstName = parts.shift() || '';
                lastName = parts.join(' ');
            } else {
                firstName = pick(k => k === 'first_name' || k.indexOf('prénom') !== -1 || k.indexOf('prenom') !== -1 || k.indexOf('first') !== -1);
                lastName = pick(k => k === 'last_name' || k.indexOf('last') !== -1 || k.indexOf('nom') !== -1);
            }

            return {
                first_name: String(firstName).trim(),
                last_name: String(lastName).trim(),
                email: String(email).trim(),
                phone: String(phone).trim()
            };
        },

        async handleCsvParsed(excelData) {
            this.csvResult = null;
            this.error = null;
            const rows = (excelData && excelData.results) || [];
            if (rows.length === 0) {
                this.$vs.notify({ title: 'Import', text: 'Fichier vide ou illisible.', color: 'warning' });
                return;
            }

            const leads = rows
                .map(r => this.extractLeadFromRow(r))
                .filter(l => l.email || l.phone);

            if (leads.length === 0) {
                this.$vs.notify({ title: 'Import', text: 'Aucune colonne email ou téléphone reconnue dans le fichier.', color: 'warning' });
                return;
            }

            this.csvImporting = true;
            let imported = 0, duplicates = 0, skipped = 0;
            try {
                const CHUNK = 200; // découpage pour rester sous la limite de taille du body
                for (let i = 0; i < leads.length; i += CHUNK) {
                    const batch = leads.slice(i, i + CHUNK);
                    const res = await MetaLead.importCsv(batch);
                    const d = (res && res.data) || {};
                    imported += d.imported || 0;
                    duplicates += d.duplicates || 0;
                    skipped += d.skipped || 0;
                }
                this.csvResult = { imported, duplicates, skipped, total: leads.length };
                this.$vs.notify({
                    title: 'Import terminé',
                    text: `${imported} prospect(s) créé(s), ${duplicates} doublon(s) ignoré(s).`,
                    color: imported > 0 ? 'success' : 'warning'
                });
            } catch (error) {
                this.error = (error && error.message) || 'Erreur lors de l\'import du fichier.';
            } finally {
                this.csvImporting = false;
            }
        },

        async disconnectPage(pageId, index) {
            this.error = null;
            this.confirmDeletePageId = null;
            try {
                await MetaLead.disconnectPage(pageId);
                this.connectedPages.splice(index, 1);

                if (this.connectedPages.length === 0) {
                    this.step = 'disconnected';
                }

                this.$vs.notify({
                    title: 'Page déconnectée',
                    text: 'La page a été retirée avec succès.',
                    color: 'success'
                });
            } catch (error) {
                this.error = error.message || 'Erreur lors de la déconnexion de la page';
            }
        },

        cancelPageSelection() {
            if (this.connectedPages.length > 0) {
                this.step = 'connected';
            } else {
                this.step = 'disconnected';
            }
            this.availablePages = [];
            this.selectedPageIds = [];
        },

        async disconnect() {
            try {
                await MetaLead.disconnect();
            } catch {
                // Ignorer les erreurs de déconnexion
            }
            this.step = 'disconnected';
            this.connectedPages = [];
            this.metaToken = null;
            this.availablePages = [];
            this.selectedPageIds = [];
            this.importResult = null;
            this.$emit('disconnected');
        }
    }
};
</script>
